import { nanoid } from 'nanoid';

import { ReportData, UnitReport, UnitReportChecks } from 'pqaAccuracy/types';
import {
  ReviewCharacteristic,
  ReviewResult,
  SuggestionValueKeys,
  Check,
  SplitMode,
  ReviewType,
} from 'types/check';
import { DocumentMetaType } from 'types/document';
import {
  groupByToArr,
  countBy,
  removeUndefined,
  weakPerObjectCache,
} from 'pqaAccuracy/utils';
import { AggregatedAssessmentPage, ChecksAlert } from 'types/check-results';
import { Review } from 'types/review-category';
import {
  AssessmentPayload,
  AssessmentSummaryPayload,
} from 'types/assessment-payload';

let report: ReportData | null = null;

export const getImportedReport = () => report;
export const setImportedReport = (newReport: ReportData) => {
  report = newReport;
};

export const accuracyReportToMetadata = weakPerObjectCache(
  (report: ReportData): DocumentMetaType & { isBranded: boolean } => ({
    id: report.document.documentId ?? '',
    version: report.document.documentVersion ?? '',
    name: report.document.documentName ?? '',
    type: report.document.type ?? '',
    subtype: report.document.subtype ?? '',
    size: report.document.size ?? 0,
    description: report.document.description ?? '',
    url: report.document.url ?? '',

    availableSsiVersions: [
      {
        productFamily: report.document.productFamily ?? '',
        versions: [
          {
            productFamily: report.document.productFamily ?? '',
            documentNumber: report.document.ssiDocumentNumber ?? '',
            name: report.document.ssiDocumentName ?? '',
          },
        ],
      },
    ],
    isBranded: false,
  })
);

export const accuracyReportToAssessment = weakPerObjectCache(
  (
    report: ReportData
  ): AssessmentPayload & {
    isBrandedDocument: boolean;
    selectedSsiDocumentNumber: string[];
    error: Error | null;
  } => ({
    jobToken: '',
    isBrandedDocument: report.document.isBranded ?? false,
    pagesCounter: report.pages.length,

    selectedSsiDocumentNumber: [
      report.document.ssiDocumentNumber ?? 'Document number not found',
    ],
    assessmentSsiVersions: [
      {
        name: report.document.ssiDocumentName ?? 'Document name not found',
        documentNumber:
          report.document.ssiDocumentNumber ?? 'Document number not found',
        productFamily: report.document.productFamily ?? 'N/A',
      },
    ],
    error: null,
    supportedReviews: {
      branding: true,
      claims: true,
      consistency: true,
      font: true,
      font_size_check: true,
      safety: true,
    },
  })
);

export const accuracyReportToPageAssessments = weakPerObjectCache(
  (report: ReportData): AggregatedAssessmentPage[] => {
    return groupByToArr(report.units, i => i.pageNumber).map(
      unitsToAggregatedPage(report)
    );
  }
);

export const accuracyReportToAssessmentSummary = weakPerObjectCache(
  (report: ReportData): AssessmentSummaryPayload & { pagesCounter: number } => {
    const initAlerts = () => ({
      [ReviewResult.fail]: 0,
      [ReviewResult.pass]: 0,
      [ReviewResult.warning]: 0,
      [ReviewResult.skip]: 0,
    });

    return {
      documentAlerts: {
        claimsReview: initAlerts(),
        safetyReview: countBy(report.units, getGeneralResult, initAlerts()),
        ssiCompleteness: initAlerts(),
        styleReview: initAlerts(),
      },
      documentReviews: {
        brandingReview: {
          checks: [],
          result: ReviewResult.pass,
        },
        metadata: { checks: [] },
        ssiCompleteness: {
          checks: [],
          messages: [],
          result: ReviewResult.pass,
        },
      },
      messages: [],
      pagesCounter: report.pages.length,
    };
  }
);

const unitsToAggregatedPage =
  (report: ReportData) =>
  (units: UnitReport[], index: number): AggregatedAssessmentPage => {
    const initAlerts = () => ({
      [ReviewResult.fail]: 0,
      [ReviewResult.pass]: 0,
      [ReviewResult.warning]: 0,
      [ReviewResult.skip]: 0,
    });
    return {
      number: index + 1,

      alerts: {
        [Review.safety]: {
          ...countBy(units, getGeneralResult, initAlerts()),
        },
        [Review.style]: {
          [ReviewResult.fail]: 0,
          [ReviewResult.pass]: 0,
          [ReviewResult.warning]: 0,
        },
        [Review.claims]: {
          [ReviewResult.fail]: 0,
          [ReviewResult.pass]: 0,
          [ReviewResult.warning]: 0,
        },
      },
      viewport: { ...report.pages[index] },

      [Review.safety]: {
        groups: units.map(unitToAlert),
        messages: [],
        errors: [],
      },
      [Review.style]: { groups: [], messages: [], errors: [] },
      [Review.claims]: { groups: [], messages: [], errors: [] },
      [Review.branding]: { groups: [], messages: [], errors: [] },
    };
  };

function takeMinimalResult(results: ReviewResult[]): ReviewResult {
  return results.reduce((acc, cur) => {
    if (cur === ReviewResult.skip) return acc;
    switch (acc) {
      case ReviewResult.pass:
        return cur;
      case ReviewResult.fail:
        return acc;
      case ReviewResult.warning:
        return cur === ReviewResult.fail ? cur : acc;
      default:
        return ReviewResult.fail;
    }
  }, ReviewResult.pass) as ReviewResult;
}

const getGeneralResult = weakPerObjectCache((unit: UnitReport) =>
  takeMinimalResult(
    removeUndefined(
      ...Object.getOwnPropertyNames(unit.checks).map(
        n =>
          (unit.checks as Record<string, { result?: ReviewResult }>)[n]?.result
      )
    )
  )
);

export function unitToAlert(unit: UnitReport): ChecksAlert {
  return {
    pqaAccuracyId: unit.unitId,
    id: nanoid(),
    externalId: nanoid(),
    result: getGeneralResult(unit),
    checks: parseToChecks(unit.checks),
    splitMode: unit.splitMode ?? SplitMode.undivided,
    borderBox: {
      left: unit.coordinates[0], // x1
      top: unit.coordinates[1], // y1
      width: unit.coordinates[2] - unit.coordinates[0], // x2 - x1
      height: unit.coordinates[3] - unit.coordinates[1], // y2 - y1
    },
  };
}

export function parseToChecks(check: UnitReportChecks): Check[] {
  const ssiApplicability = (check: UnitReportChecks): Check | undefined =>
    check.ssiApplicability && {
      result: check.ssiApplicability.result,
      reviewType: ReviewType.safety,
      characteristic: ReviewCharacteristic.ssiApplicability,
      ssiInstruction: check.ssiApplicability.required,
    };

  const ssiCheck = (check: UnitReportChecks): Check | undefined =>
    check.ssiCheck && {
      result: check.ssiCheck.result,
      reviewType: ReviewType.safety,
      characteristic: ReviewCharacteristic.ssiCheck,
      usedValues: [
        { [SuggestionValueKeys.ssiCheck]: check.ssiCheck.used ?? '' },
      ],
      requiredValues: [
        { [SuggestionValueKeys.ssiCheck]: check.ssiCheck.required ?? '' },
      ],
    };

  return removeUndefined(ssiApplicability(check), ssiCheck(check));
}
